import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type Ctx = {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  isEditor: boolean;
  editorSections: string[];
  loading: boolean;
  mustChangePassword: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  refreshPasswordStatus: () => Promise<void>;

};

const AuthCtx = createContext<Ctx | null>(null);

/** Marks a password-recovery session that must NOT grant access outside /reset-password. */
export const RECOVERY_FLAG = "lc.recoveryPending";
export function isRecoveryPending() {
  try { return localStorage.getItem(RECOVERY_FLAG) === "1"; } catch { return false; }
}
export function setRecoveryPending(on: boolean) {
  try { on ? localStorage.setItem(RECOVERY_FLAG, "1") : localStorage.removeItem(RECOVERY_FLAG); } catch { /* noop */ }
}
function onResetPage() {
  return typeof window !== "undefined" && window.location.pathname === "/reset-password";
}
/** Recovery session found outside the reset page: discard it. */
async function killStrayRecovery(): Promise<boolean> {
  if (isRecoveryPending() && !onResetPage()) {
    await supabase.auth.signOut();
    setRecoveryPending(false);
    return true;
  }
  return false;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [editorSections, setEditorSections] = useState<string[]>([]);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If we already land with recovery tokens (code / token_hash / hash),
    // redirect to /reset-password preserving them BEFORE supabase-js processes them.
    if (typeof window !== "undefined" && window.location.pathname !== "/reset-password") {
      const search = window.location.search;
      const hash = window.location.hash;
      const isRecovery =
        /[?&]code=/.test(search) ||
        (/[?&]type=recovery/.test(search) && /[?&]token_hash=/.test(search)) ||
        /type=recovery/.test(hash);
      if (isRecovery) {
        window.location.replace(`/reset-password${search}${hash}`);
      }
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === "PASSWORD_RECOVERY") setRecoveryPending(true);
      if (s && isRecoveryPending()) {
        // Recovery sessions never count as logged in.
        setSession(null);
        if (!onResetPage()) setTimeout(() => { killStrayRecovery(); }, 0);
        return;
      }
      setSession(s);
      if (event === "PASSWORD_RECOVERY") {
        if (window.location.pathname !== "/reset-password") {
          window.location.replace(
            `/reset-password${window.location.search}${window.location.hash || ""}`,
          );
        }
      }
      if (s?.user) {
        setTimeout(() => {
          loadRoles(s.user.id);
          loadPermissions(s.user.id);
          loadPasswordStatus(s.user.id);
        }, 0);
      } else {
        setRoles([]);
        setEditorSections([]);
        setMustChangePassword(false);
      }
    });
    (async () => {
      await killStrayRecovery();
      return supabase.auth.getSession();
    })().then(({ data }) => {
      if (isRecoveryPending()) { setSession(null); setLoading(false); return; }
      setSession(data.session);
      if (data.session?.user) {
        loadRoles(data.session.user.id);
        loadPermissions(data.session.user.id);
        loadPasswordStatus(data.session.user.id);
      }
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function loadRoles(uid: string) {
    const { data } = await supabase.from("user_roles").select("role").eq("user_id", uid);
    setRoles((data ?? []).map((r) => r.role));
  }

  async function loadPermissions(uid: string) {
    const { data } = await supabase
      .from("editor_permissions" as never)
      .select("section")
      .eq("user_id", uid);
    setEditorSections(((data as any[]) ?? []).map((r) => r.section));
  }

  async function loadPasswordStatus(uid: string) {
    const { data } = await supabase
      .from("admin_password_status")
      .select("must_change")
      .eq("user_id", uid)
      .maybeSingle();
    setMustChangePassword(!!data?.must_change);
  }

  const value: Ctx = {
    session,
    user: session?.user ?? null,
    isAdmin: roles.includes("admin"),
    isEditor: roles.includes("admin") || roles.includes("editor"),
    editorSections,
    loading,
    mustChangePassword,
    async signIn(email, password) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error?.message };
    },
    async signUp() {
      return { error: "Cadastro público desativado. Solicite acesso ao administrador." };
    },

    async signOut() {
      await supabase.auth.signOut();
    },
    async refreshPasswordStatus() {
      if (session?.user) await loadPasswordStatus(session.user.id);
    },
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
